import { useEffect } from "react";
import { NavigateFunction } from "react-router-dom";

import { DropdownWithCheckboxesItem } from "@/components/DropdownWithCheckboxes/DropdownWithCheckboxes";

import { IFilterWithValues } from "../../../List/components/FilterModal";
import { CustomListsAutocompleteForSequencerItem } from "../../../List/components/CustomListsAutocompleteForSequencer";

interface UseInboxUrlSyncArgs {
	selectedSenders: DropdownWithCheckboxesItem[];
	searchValue: string;
	filter: IFilterWithValues | undefined;
	selectedLists: CustomListsAutocompleteForSequencerItem[];
	setSearchValue: (value: string) => void;
	navigate: NavigateFunction;
}

/**
 * Keeps the Inbox's senders / search / filter / list-scope selections mirrored into the URL
 * query string (so a reload or a shared link restores the same view), and seeds the search box
 * from the URL on first mount.
 */
export function useInboxUrlSync({
	selectedSenders,
	searchValue,
	filter,
	selectedLists,
	setSearchValue,
	navigate,
}: UseInboxUrlSyncArgs) {
	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const urlSearchValue = params.get("searchValue") || "";

		if (urlSearchValue) {
			setSearchValue(urlSearchValue);
		}
	}, []);

	useEffect(() => {
		const params = new URLSearchParams(location.search);

		params.set("senders", JSON.stringify(selectedSenders));

		navigate({ search: params.toString() }, { replace: true });
	}, [selectedSenders, location.search, navigate]);

	useEffect(() => {
		const params = new URLSearchParams(location.search);

		if (searchValue) {
			params.set("searchValue", searchValue);
		} else {
			params.delete("searchValue");
		}

		navigate({ search: params.toString() }, { replace: true });
	}, [searchValue, location.search, navigate]);

	useEffect(() => {
		const params = new URLSearchParams(location.search);

		if (filter) {
			params.set("filter", JSON.stringify(filter));
		} else {
			params.delete("filter");
		}

		navigate({ search: params.toString() }, { replace: true });
	}, [filter, location.search, navigate]);

	useEffect(() => {
		const params = new URLSearchParams(location.search);

		if (selectedLists.length > 0) {
			params.set("listIds", JSON.stringify(selectedLists));
		} else {
			params.delete("listIds");
		}

		navigate({ search: params.toString() }, { replace: true });
	}, [selectedLists, navigate]);
}
